import React, { useEffect, useState } from 'react';
import { Box, useTheme } from '@mui/material';
import Text from '../../theme/text/Text';
import { useTranslation } from "react-i18next";
import { useUpdateSettingForRelationMutation, useUpdateSettingMutation } from '../../../newapi/global/generalApiSlice';
import { tokens } from '../../../theme';
import { InputDropdown } from "../../theme/dropdowns/InputDropdown";
import EMGeneralExplainerBox from "../ModalsPopover/explainers/EMGeneralExplainerBox";

// variants
//    1: default has both a title and subtitle
//    2: has no subtitle ans text is light
//
// manualSave: never persists on its own. The selection still follows the click, and the
//   picked value is reported through `onChange(value, setting)` for a screen that saves
//   several settings behind one Save button of its own, as useManualSettingsSave does.
const DropdownSetting = (props) => {
  const { title = true, subtitle = true, variant = 1, showNullAsSelected = false, manualSave = false} = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [updateSetting] = useUpdateSettingMutation();
  const [updateSettingForRelation] = useUpdateSettingForRelationMutation();

  const [setting, setSetting] = useState();
  // The picked value is held here so the dropdown follows the click even when the
  // screen has yet to persist it.
  const [value, setValue] = useState();
  const [objectBody, setObjectBody] = useState([]);

  const handleChange = async (picked) => {
    if (props.onChange) {
      props.onChange(props.multiple ? picked : picked?.id, props.relationId ? { ...setting, relationId: props.relationId } : setting);
    }
    // Multi-select: picked is the array of selected ids, persisted into objectBody.
    if (props.multiple) {
      setObjectBody(picked);
      if (manualSave) {
        return;
      }
      if(props.relationId){
        await updateSettingForRelation({
          key: setting.key,
          objectBody: picked,
          value: "",
          relationId: props.relationId
        });
      }else{
        await updateSetting({
          key: setting.key,
          objectBody: picked,
          value: "",
          warehouseId: setting.warehouseId,
        });
      }
      return;
    }
    setValue(picked?.id);
    if (manualSave) {
      return;
    }
    if(props.relationId){
      await updateSettingForRelation({
        key: setting.key,
        value: picked?.id,
        relationId: props.relationId
      });
    }else{
      await updateSetting({
        key: setting.key,
        value: picked?.id,
        warehouseId: setting.warehouseId,
      });
    }
  };

  // Multi-select hands the InputDropdown the array of ids straight through; single
  // maps the picked value/index back to the option object before saving.
  const handleDropdownChange = (selected) => {
    if (props.multiple) {
      handleChange(selected);
      return;
    }
    if (props.valueKey) {
      handleChange(props.objects.find(object => object[props.valueKey] === selected));
      return;
    }
    handleChange(props.objects[selected]);
  };

  let selectedValue;
  if (props.multiple) {
    selectedValue = objectBody ?? [];
  } else if (props.valueKey) {
    selectedValue = props.objects?.find(object => object.id === parseInt(value))?.[props.valueKey];
    if (typeof selectedValue === "undefined" && showNullAsSelected) {
      selectedValue = null;
    }
  } else {
    selectedValue = props.objects?.findIndex(object => value === object.id) ?? 0;
  }

  useEffect(() => {
    if(props.skey && typeof props.settings !== "undefined"){
        let xsetting = props.settings.find((setting) => setting.key === props.skey);
        if (!xsetting) {
            xsetting = { key: props.skey, value: 0, warehouseId: -1 };
        }
        setSetting(xsetting);
        setValue(xsetting.value);
        setObjectBody(xsetting.objectBody ?? []);
    }
  }, [props.settings, props.skey]);

  return (
    <Box
      sx={{
        width: 1,
      }}
    >
      <Box
        sx={{
          display:"none",
          "@media screen and (max-width: 47.9375em)": {
            display:"block"
          },
        }}
      >
        <Box
          sx={{
            ml:"-12px",
            mr:"-12px",
            pl:2,
            pr:1,
            display:"flex",
            py:1,
            borderRadius:7,
            gap:2,
            alignItems:"center",
            background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
            border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
            boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
          }}
        >
          <Text
            sx={props.explainer ? {display: "flex", alignItems: "center"} : {}} 
            noBreak
            semibold={props.variant !== 2}
            // light={variant === 2}
            
          >
              {t(`${setting?.key}_title`)}
              {props.explainer && <EMGeneralExplainerBox title={props.explainerTitle} text={props.explainerText}/>}
          </Text>
          <Box
            sx={{
              flex: 1,
              maxWidth: props.maxWidth ?? 180,
              marginLeft: "auto",
              backgroundColor: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
              borderRadius: 4,
              display: "flex",
              height: 32,
            
            }}
          >
            <InputDropdown
                bold={props.bold ?? false}
                multiple={props.multiple}
                allowSelectAll={props.allowSelectAll}
                valueKey={props.valueKey}
                showNullAsSelected={showNullAsSelected}
                variant={"default"}
                displayName={"name"} 
                key={value}
                searchOptions={props.searchOptions}
                styles={props.styles}
                onChange={handleDropdownChange}
                noSelection={t(props.noselection)}
                options={props.objects ?? null}
                selected={selectedValue}
                closeOnSelection={!props.multiple}
            />
          </Box>

        </Box>
        {subtitle && variant !== 2 && 
          <Text 
            light
            sx={{
              pt:1,
            }}
          >
            {t(`${setting?.key}_subtitle`)}
          </Text>
        }

      </Box>

      <Box
        sx={{
          width: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 4,
          "@media screen and (max-width: 47.9375em)": {
            display:"none"
          },
        }}
      >
        <Box
          sx={{
            display:"flex",
            flexDirection:"column",
          }}
        >
          <Box sx={props.explainer ? {display: "flex", alignItems: "center"} : {}}>
            {title && <Text bool size={variant === 2 ?? "fs13"}  semibold={props.variant !== 2} >{t(`${setting?.key}_title`)} </Text>}
            {props.explainer && <EMGeneralExplainerBox title={props.explainerTitle} text={props.explainerText}/>}
          </Box>
          {subtitle && variant !== 2 && <Text light>{t(`${setting?.key}_subtitle`)}</Text>}
        </Box>

        <Box
          sx={{
            flex: 1,
            maxWidth: props.maxWidth ?? 250,
            marginLeft: "auto",
            backgroundColor: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
            borderRadius: 4,
            display: "flex",
            height: 32,
          
          }}
        >
          <InputDropdown
              bold={props.bold ?? true}
              multiple={props.multiple}
              allowSelectAll={props.allowSelectAll}
              valueKey={props.valueKey}
              showNullAsSelected={showNullAsSelected}
              variant={"default"}
              displayName={"name"} 
              key={value}
              searchOptions={props.searchOptions}
              styles={props.styles}
              onChange={handleDropdownChange}
              noSelection={t(props.noselection)}
              options={props.objects ?? null}
              selected={selectedValue}
              closeOnSelection={!props.multiple}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default DropdownSetting;